import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProjectBriefDocument } from './ProjectBriefDocument';
import {
  fetchProjectShare,
  submitProjectShare,
  type ProjectShareBrief,
} from '../lib/projectShare';
import { useI18n } from '../hooks/useI18n';

export function ProjectSharePage() {
  const { token = '' } = useParams();
  const { t, language } = useI18n();
  const [brief, setBrief] = useState<ProjectShareBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<'comment' | 'requestChanges' | 'approve' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        setError(t('projectShare.missingToken', 'This link is incomplete.'));
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProjectShare(token);
        if (!cancelled) {
          setBrief(data);
          setEmail((current) => current || data.client.email || '');
          setName((current) => current || data.client.contact || data.client.name || '');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('projectShare.loadFailed', 'Could not load this brief.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const locale = brief?.client.locale || (language === 'en' ? 'en' : 'es');
  const approved = brief?.shareStatus === 'approved';
  const inputClass =
    'w-full border border-neutral-300 bg-white px-3 py-2 text-sm text-[#111111] focus:border-[#1b7a74] focus:outline-none';

  const runAction = async (action: 'comment' | 'requestChanges' | 'approve') => {
    if (!token) return;
    setBusy(action);
    setNotice(null);
    setError(null);
    try {
      const next = await submitProjectShare({ action, token, name, email, body: message });
      setBrief(next);
      setMessage('');
      setNotice(
        action === 'approve'
          ? t('projectShare.approvedThanks', 'Thank you. This brief is now approved.')
          : action === 'requestChanges'
            ? t('projectShare.changesSent', 'Your change request was sent.')
            : t('projectShare.commentSent', 'Your comment was sent.')
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projectShare.actionFailed', 'Could not submit.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#e8e4db] px-4 py-8 text-[#111111]">
      <div className="mx-auto max-w-4xl space-y-6">
        {loading ? <p className="text-sm text-neutral-600">{t('projectShare.loading', 'Loading brief...')}</p> : null}
        {error ? <p className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p> : null}
        {notice ? <p className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">{notice}</p> : null}

        {brief ? (
          <>
            <ProjectBriefDocument
              projectName={brief.project.name}
              description={brief.project.description}
              client={brief.client}
              brief={brief.brief}
              lineItems={brief.lineItems}
              quote={brief.quote}
              contractText={brief.contractText}
              branding={brief.branding}
              shareStatus={brief.shareStatus}
              approved={brief.approved}
              locale={locale}
            />

            {brief.feedback.length ? (
              <section className="bg-[#f4f1ea] px-8 py-6">
                <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                  {t('projectShare.activity', 'Activity')}
                </p>
                <ul className="mt-3 space-y-3 text-sm">
                  {brief.feedback.map((item) => (
                    <li key={item.id} className="border-b border-neutral-200 pb-3 last:border-0">
                      <p className="text-xs uppercase tracking-wide text-neutral-500">
                        {item.kind === 'approval'
                          ? t('projectShare.kind.approval', 'Approval')
                          : item.kind === 'change_request'
                            ? t('projectShare.kind.changes', 'Change request')
                            : t('projectShare.kind.comment', 'Comment')}
                        {item.author_name ? ` · ${item.author_name}` : ''}
                      </p>
                      {item.body ? <p className="mt-1">{item.body}</p> : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {approved ? null : (
              <section className="bg-[#f4f1ea] px-8 py-6">
                <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                  {t('projectShare.respond', 'Respond')}
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="text-xs text-neutral-600">
                    {t('projectShare.yourName', 'Your name')}
                    <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} mt-1`} />
                  </label>
                  <label className="text-xs text-neutral-600">
                    {t('projectShare.yourEmail', 'Your email')}
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${inputClass} mt-1`}
                    />
                  </label>
                </div>
                <label className="mt-3 block text-xs text-neutral-600">
                  {t('projectShare.message', 'Comment or requested changes')}
                  <textarea
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className={`${inputClass} mt-1 resize-y`}
                  />
                </label>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => void runAction('comment')}
                    className="border border-neutral-300 px-4 py-2 text-sm hover:border-neutral-500 disabled:opacity-50"
                  >
                    {t('projectShare.sendComment', 'Send comment')}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => void runAction('requestChanges')}
                    className="border border-neutral-300 px-4 py-2 text-sm hover:border-neutral-500 disabled:opacity-50"
                  >
                    {t('projectShare.requestChanges', 'Request changes')}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => void runAction('approve')}
                    className="bg-[#0B0F14] px-4 py-2 text-sm font-medium text-[#f4f1ea] disabled:opacity-50"
                  >
                    {t('projectShare.approve', 'Approve brief')}
                  </button>
                </div>
              </section>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
